export const TEST_TYPES = [
  "Final Test",
  "Overall Final Test",
  "Weekly Test",
  "Revision Test",
  "Practice Test",
] as const;

export type TestType = (typeof TEST_TYPES)[number];

/** Final Test and Overall Final Test share fixed-window / rank report behavior. */
export const isFinalLikeTestType = (testType?: string | null): boolean => {
  const normalized = (testType || "").trim();
  return (
    normalized === "Final Test" || normalized === "Overall Final Test"
  );
};

export const isPracticeTestType = (testType?: string | null): boolean =>
  (testType || "").trim() === "Practice Test";

/** SFT / OFT / Practice: slot-based trainer report release. WT / RT do not use this. */
export const isSlotReleaseTestType = (testType?: string | null): boolean =>
  isFinalLikeTestType(testType) || isPracticeTestType(testType);

/** Slot toolbar: Final/OFT = attendance + Start; Practice = optional attendance; others = none. */
export type SlotAttendanceUiMode = "none" | "final" | "practice";

export const slotAttendanceUiMode = (
  testType?: string | null
): SlotAttendanceUiMode => {
  if (isFinalLikeTestType(testType)) return "final";
  if (isPracticeTestType(testType)) return "practice";
  return "none";
};

/** True once the slot window has begun (live/completed, or start_time reached).

  Shared lock for Practice attendance, slot delete, and batch add/remove. */
export const isPracticeAttendanceDecisionLocked = (
  slot: { status?: string | null; start_time?: string | null },
  nowMs: number = Date.now()
): boolean => {
  const status = (slot.status || "").trim().toLowerCase();
  if (status === "live" || status === "completed") return true;
  if (!slot.start_time) return false;
  const start = new Date(slot.start_time);
  if (Number.isNaN(start.getTime())) return false;
  return nowMs >= start.getTime();
};

/** Practice Test duration timer is allowed only when the slot window is under 24 hours. */
export const PRACTICE_DURATION_MAX_MS = 24 * 60 * 60 * 1000;

export const practiceWindowAllowsDuration = (
  start?: string | null,
  end?: string | null
): boolean => {
  if (!start || !end) return false;
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  const delta = endMs - startMs;
  return delta > 0 && delta < PRACTICE_DURATION_MAX_MS;
};

/** Weekly / Revision: report unlocks as soon as the student completes (no trainer release). */
export const isImmediateAutoReleaseTestType = (
  testType?: string | null
): boolean => {
  const normalized = (testType || "").trim().toLowerCase();
  return normalized === "weekly test" || normalized === "revision test";
};

/** Match backend sanitize for auto test names. */
export const sanitizeTestNameToken = (value: string): string => {
  const token = (value || "").trim().replace(/ /g, "").replace(/-/g, "_");
  return token || "X";
};

/**
 * Build auto test name (same format as backend).
 * `prefix` and `sequence` must come from API data — not hardcoded.
 */
export const buildAutoTestName = (
  prefix: string,
  courseNames: string[],
  subjectNames: string[],
  sequence: number
): string => {
  const coursesPart = [...courseNames]
    .map(sanitizeTestNameToken)
    .sort()
    .join("-");
  const subjectsPart = [...subjectNames]
    .map(sanitizeTestNameToken)
    .sort()
    .join("-");
  const seq = String(Math.max(1, sequence)).padStart(3, "0");
  return `${prefix}-${coursesPart}-${seq}-${subjectsPart}`;
};

/** Admin test-report student display statuses */
export const REPORT_STUDENT_STATUS = {
  ASSIGNED: "Assigned",
  ABSENT: "Absent",
  IN_PROGRESS: "In Progress",
  ABORTED: "Aborted",
  TERMINATED: "Terminated",
  COMPLETED: "Completed",
} as const;

export const REPORT_STUDENT_STATUSES = Object.values(REPORT_STUDENT_STATUS);

export type ReportStudentDisplayStatus =
  (typeof REPORT_STUDENT_STATUS)[keyof typeof REPORT_STUDENT_STATUS];

/** Raw assessment_status values stored in students_assessments */
export const ASSESSMENT_STATUS = {
  PENDING: "Pending",
  STARTED: "Started",
  COMPLETED: "Completed",
  ABORTED: "Aborted",
  VIOLATED: "Violated",
} as const;
